/**
 * Deals — the sales pipeline (9.4k cold-call prospects, lib BE: /admin/deals).
 * The one admin surface a sales login can reach; admins see it too.
 *
 * Starter build for Ben to shape: summary tiles, debounced search, stage /
 * software / manager filters, sortable columns, pagination, inline stage +
 * assign, notes drawer, and "Generate profile" (materialises the hidden
 * preview salon and hands back the shareable slug link).
 */
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import AdminWrapper from './AdminWrapper';
import { adminService } from '../../_services/admin.service';
import './Admin.css';

const STAGES = ['cold', 'contacted', 'demo', 'closing', 'won', 'lost'];
const STAGE_COLOURS = {
    cold: '#90a4ae', contacted: '#42a5f5', demo: '#ab47bc',
    closing: '#ffa726', won: '#66bb6a', lost: '#ef5350'
};

class AdminDeals extends Component {
    constructor(props) {
        super(props);
        this.state = {
            deals: [], total: 0, page: 1, pages: 1, limit: 50,
            summary: null, managers: [],
            search: '', stage: '', software: '', managerFilter: '',
            sortBy: 'updatedTime', sortOrder: 'desc',
            loading: true, error: null,
            openDeal: null, noteText: '', busyId: null, toast: null
        };
        this.searchTimeout = null;
    }

    componentDidMount() {
        this.fetchDeals();
        this.fetchSummary();
        adminService.getDealManagers()
            .then((r) => { if (r.statusCode === 1) this.setState({ managers: r.responseData || [] }); })
            .catch(() => {});
    }

    fetchDeals = async () => {
        const { page, limit, search, stage, software, managerFilter, sortBy, sortOrder } = this.state;
        this.setState({ loading: true, error: null });
        try {
            const r = await adminService.getDeals({
                page, limit, q: search, stage, software,
                salesManagerId: managerFilter, sortBy, sortOrder
            });
            if (r.statusCode === 1) {
                const d = r.responseData || {};
                this.setState({ deals: d.deals || [], total: d.total || 0, pages: d.pages || 1, loading: false });
            } else {
                this.setState({ error: 'Could not load deals', loading: false });
            }
        } catch (e) {
            this.setState({ error: String(e), loading: false });
        }
    }

    fetchSummary = () => {
        adminService.getDealsSummary()
            .then((r) => { if (r.statusCode === 1) this.setState({ summary: r.responseData }); })
            .catch(() => {});
    }

    handleSearchChange = (e) => {
        const search = e.target.value;
        this.setState({ search, page: 1 });
        if (this.searchTimeout) clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(this.fetchDeals, 400);
    }

    setFilter = (key, value) => this.setState({ [key]: value, page: 1 }, this.fetchDeals);

    handleSort = (col) => {
        const { sortBy, sortOrder } = this.state;
        const next = sortBy === col && sortOrder === 'asc' ? 'desc' : 'asc';
        this.setState({ sortBy: col, sortOrder: next, page: 1 }, this.fetchDeals);
    }

    handlePage = (p) => {
        if (p < 1 || p > this.state.pages) return;
        this.setState({ page: p }, this.fetchDeals);
    }

    patchDeal = async (dealId, body) => {
        this.setState({ busyId: dealId });
        try {
            const r = await adminService.updateDeal(dealId, body);
            if (r.statusCode === 1) {
                this.setState((s) => ({
                    deals: s.deals.map((d) => (d._id === dealId ? { ...d, ...r.responseData } : d)),
                    openDeal: s.openDeal && s.openDeal._id === dealId ? { ...s.openDeal, ...r.responseData } : s.openDeal
                }));
                this.fetchSummary();
            }
        } finally {
            this.setState({ busyId: null });
        }
    }

    addNote = async () => {
        const { openDeal, noteText } = this.state;
        if (!openDeal || !noteText.trim()) return;
        const r = await adminService.addDealNote(openDeal._id, noteText.trim());
        if (r.statusCode === 1) {
            this.setState((s) => ({
                noteText: '',
                openDeal: { ...s.openDeal, notes: r.responseData.notes },
                deals: s.deals.map((d) => (d._id === openDeal._id ? { ...d, notes: r.responseData.notes } : d))
            }));
        }
    }

    materialise = async (deal) => {
        this.setState({ busyId: deal._id });
        try {
            const r = await adminService.materialiseDeal(deal._id);
            if (r.statusCode === 1) {
                const { slug, salonId } = r.responseData;
                const url = `${window.location.origin}/salon/${slug}`;
                try { await navigator.clipboard.writeText(url); } catch (e) { /* no clipboard */ }
                this.setState((s) => ({
                    toast: `Profile ready — link copied: ${url}`,
                    deals: s.deals.map((d) => (d._id === deal._id ? { ...d, salonId, salonSlug: slug } : d)),
                    openDeal: s.openDeal && s.openDeal._id === deal._id ? { ...s.openDeal, salonId, salonSlug: slug } : s.openDeal
                }));
                setTimeout(() => this.setState({ toast: null }), 6000);
            }
        } finally {
            this.setState({ busyId: null });
        }
    }

    stageCount = (stage) => {
        const rows = this.state.summary?.byStage || [];
        const hit = rows.find((r) => r._id === stage);
        return hit ? hit.n : 0;
    }

    renderSortHeader(label, col) {
        const { sortBy, sortOrder } = this.state;
        return (
            <th onClick={() => this.handleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                {label}{sortBy === col ? (sortOrder === 'asc' ? ' ▲' : ' ▼') : ''}
            </th>
        );
    }

    render() {
        const {
            deals, total, page, pages, summary, managers, search, stage, software,
            managerFilter, loading, error, openDeal, noteText, busyId, toast
        } = this.state;
        const softwares = (summary?.bySoftware || []).filter((s) => s._id);

        return (
            <AdminWrapper>
                <div className="admin-page">
                    <div className="admin-page-header">
                        <h1 className="admin-page-title">Deals</h1>
                        <p className="admin-page-subtitle">The sales pipeline — every prospect, cold to won</p>
                    </div>

                    {/* Stage tiles double as filters */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                        {STAGES.map((s) => (
                            <button
                                key={s}
                                onClick={() => this.setFilter('stage', stage === s ? '' : s)}
                                style={{
                                    flex: '1 1 120px', padding: '14px 16px', borderRadius: '12px', textAlign: 'left',
                                    border: stage === s ? `2px solid ${STAGE_COLOURS[s]}` : '1px solid #e0e0e0',
                                    background: '#fff', cursor: 'pointer'
                                }}
                            >
                                <div style={{ fontSize: '12px', textTransform: 'capitalize', color: STAGE_COLOURS[s], fontWeight: 600 }}>{s}</div>
                                <div style={{ fontSize: '22px', fontWeight: 700 }}>{this.stageCount(s).toLocaleString('en-GB')}</div>
                            </button>
                        ))}
                    </div>

                    <div className="admin-table-container">
                        <div className="admin-table-header">
                            <h3 className="admin-table-title">{total.toLocaleString('en-GB')} {total === 1 ? 'Deal' : 'Deals'}</h3>
                            <div className="admin-table-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                                <select value={software} onChange={(e) => this.setFilter('software', e.target.value)}
                                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', background: '#fff' }}>
                                    <option value="">All software</option>
                                    {softwares.map((s) => (
                                        <option key={s._id} value={s._id}>{s._id} ({s.n.toLocaleString('en-GB')})</option>
                                    ))}
                                </select>
                                <select value={managerFilter} onChange={(e) => this.setFilter('managerFilter', e.target.value)}
                                    style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', background: '#fff' }}>
                                    <option value="">All managers</option>
                                    <option value="unassigned">Unassigned</option>
                                    {managers.map((m) => (
                                        <option key={m._id} value={m._id}>{m.name || m.email}</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    className="admin-search-input"
                                    placeholder="Search name, postcode, town or email…"
                                    value={search}
                                    onChange={this.handleSearchChange}
                                />
                            </div>
                        </div>

                        {loading ? (
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                <div className="admin-loading-spinner" style={{ margin: '0 auto 16px' }}></div>
                                <p style={{ color: '#666' }}>Loading deals…</p>
                            </div>
                        ) : error ? (
                            <div style={{ padding: '40px', textAlign: 'center' }}>
                                <p style={{ color: '#ff6b6b', marginBottom: '16px' }}>{error}</p>
                                <button className="admin-btn admin-btn-secondary" onClick={this.fetchDeals}>Retry</button>
                            </div>
                        ) : deals.length === 0 ? (
                            <div className="admin-empty-state">
                                <h3 className="admin-empty-title">No deals match</h3>
                                <p className="admin-empty-text">Try clearing the filters or search</p>
                            </div>
                        ) : (
                            <>
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="admin-table">
                                        <thead>
                                            <tr>
                                                {this.renderSortHeader('Salon', 'name')}
                                                {this.renderSortHeader('Location', 'city')}
                                                {this.renderSortHeader('Software', 'currentSoftware')}
                                                <th>Contact</th>
                                                {this.renderSortHeader('Stage', 'stage')}
                                                <th>Manager</th>
                                                {this.renderSortHeader('Next action', 'nextActionAt')}
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {deals.map((deal) => (
                                                <tr key={deal._id} onClick={() => this.setState({ openDeal: deal, noteText: '' })} style={{ cursor: 'pointer' }}>
                                                    <td>
                                                        <div style={{ fontWeight: 500 }}>{deal.name}</div>
                                                        {deal.notes?.length > 0 && (
                                                            <div style={{ fontSize: '12px', color: '#999' }}>{deal.notes.length} note{deal.notes.length === 1 ? '' : 's'}</div>
                                                        )}
                                                    </td>
                                                    <td>{[deal.city, deal.postcode].filter(Boolean).join(', ') || '—'}</td>
                                                    <td style={{ textTransform: 'capitalize' }}>{deal.currentSoftware || '—'}</td>
                                                    <td>
                                                        <div style={{ fontSize: '13px' }}>{deal.phone || '—'}</div>
                                                        <div style={{ fontSize: '12px', color: '#666' }}>{deal.email || ''}</div>
                                                    </td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <select
                                                            value={deal.stage}
                                                            disabled={busyId === deal._id}
                                                            onChange={(e) => this.patchDeal(deal._id, { stage: e.target.value })}
                                                            style={{
                                                                padding: '6px 8px', borderRadius: '8px', fontSize: '13px',
                                                                border: `1px solid ${STAGE_COLOURS[deal.stage] || '#ddd'}`,
                                                                color: STAGE_COLOURS[deal.stage] || '#333', fontWeight: 600,
                                                                textTransform: 'capitalize', background: '#fff'
                                                            }}
                                                        >
                                                            {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
                                                        </select>
                                                    </td>
                                                    <td onClick={(e) => e.stopPropagation()}>
                                                        <select
                                                            value={deal.salesManagerId?._id || deal.salesManagerId || ''}
                                                            disabled={busyId === deal._id}
                                                            onChange={(e) => this.patchDeal(deal._id, { salesManagerId: e.target.value || null })}
                                                            style={{ padding: '6px 8px', borderRadius: '8px', fontSize: '13px', border: '1px solid #ddd', background: '#fff', maxWidth: '140px' }}
                                                        >
                                                            <option value="">Unassigned</option>
                                                            {managers.map((m) => (
                                                                <option key={m._id} value={m._id}>{m.name || m.email}</option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                    <td style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                        {deal.nextActionAt ? new Date(deal.nextActionAt).toLocaleDateString('en-GB') : '—'}
                                                    </td>
                                                    <td onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                                                        <button
                                                            className="admin-btn admin-btn-secondary"
                                                            disabled={busyId === deal._id}
                                                            onClick={() => this.materialise(deal)}
                                                            style={{ fontSize: '12px', padding: '6px 10px' }}
                                                        >
                                                            {deal.salonId ? 'Profile link' : 'Generate profile'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px' }}>
                                    <span style={{ fontSize: '13px', color: '#666' }}>Page {page} of {pages.toLocaleString('en-GB')}</span>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button className="admin-btn admin-btn-secondary" disabled={page <= 1} onClick={() => this.handlePage(page - 1)}>Previous</button>
                                        <button className="admin-btn admin-btn-secondary" disabled={page >= pages} onClick={() => this.handlePage(page + 1)}>Next</button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {/* Notes / detail drawer */}
                    {openDeal && (
                        <div
                            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9998 }}
                            onClick={() => this.setState({ openDeal: null })}
                        >
                            <div
                                style={{
                                    position: 'absolute', top: 0, right: 0, bottom: 0, width: 'min(420px, 92vw)',
                                    background: '#fff', boxShadow: '-8px 0 24px rgba(0,0,0,0.15)', padding: '24px', overflowY: 'auto'
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <h2 style={{ margin: '0 0 4px', fontSize: '20px' }}>{openDeal.name}</h2>
                                <div style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
                                    {[openDeal.address, openDeal.city, openDeal.postcode].filter(Boolean).join(', ')}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '13px', marginBottom: '16px' }}>
                                    <div>Software: <strong style={{ textTransform: 'capitalize' }}>{openDeal.currentSoftware || '—'}</strong></div>
                                    <div>Stage: <strong style={{ textTransform: 'capitalize', color: STAGE_COLOURS[openDeal.stage] }}>{openDeal.stage}</strong></div>
                                    <div>Phone: <strong>{openDeal.phone || '—'}</strong></div>
                                    <div>Email: <strong style={{ wordBreak: 'break-all' }}>{openDeal.email || '—'}</strong></div>
                                    {openDeal.website && <div style={{ gridColumn: '1 / -1' }}>Web: <a href={openDeal.website.startsWith('http') ? openDeal.website : `https://${openDeal.website}`} target="_blank" rel="noopener noreferrer">{openDeal.website}</a></div>}
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '6px' }}>Next action</label>
                                    <input
                                        type="date"
                                        value={openDeal.nextActionAt ? String(openDeal.nextActionAt).slice(0, 10) : ''}
                                        onChange={(e) => this.patchDeal(openDeal._id, { nextActionAt: e.target.value || null })}
                                        style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
                                    />
                                </div>

                                <h3 style={{ fontSize: '15px', margin: '0 0 8px' }}>Notes</h3>
                                <div style={{ marginBottom: '12px' }}>
                                    {(openDeal.notes || []).slice().reverse().map((n, i) => (
                                        <div key={n._id || i} style={{ padding: '10px 12px', background: '#f7f7f8', borderRadius: '10px', marginBottom: '8px' }}>
                                            <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{n.text}</div>
                                            <div style={{ fontSize: '11px', color: '#999', marginTop: '4px' }}>{n.at ? new Date(n.at).toLocaleString('en-GB') : ''}</div>
                                        </div>
                                    ))}
                                    {!(openDeal.notes || []).length && <div style={{ fontSize: '13px', color: '#999' }}>No notes yet</div>}
                                </div>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input
                                        type="text"
                                        placeholder="Add a note…"
                                        value={noteText}
                                        onChange={(e) => this.setState({ noteText: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') this.addNote(); }}
                                        style={{ flex: 1, padding: '10px 12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px' }}
                                    />
                                    <button className="admin-btn admin-btn-primary" onClick={this.addNote} disabled={!noteText.trim()}>Add</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {toast && (
                        <div style={{
                            position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
                            background: '#1a1a1a', color: '#fff', padding: '12px 20px', borderRadius: '12px',
                            fontSize: '14px', zIndex: 9999, maxWidth: '90vw', wordBreak: 'break-all'
                        }}>
                            {toast}
                        </div>
                    )}
                </div>
            </AdminWrapper>
        );
    }
}

export default withRouter(AdminDeals);
